<template>
  <div id="PayOrderMgmt" class="purchase-mgmt-page">
    <div class="page-header">
      <div class="filter-card">
        <div class="filter-row">
          <div class="filter-item">
            <span class="filter-label">创建日期</span>
            <el-date-picker v-model="createDate" size="small" type="daterange" range-separator="至" start-placeholder="开始" end-placeholder="结束" class="filter-date"></el-date-picker>
          </div>
          <div class="filter-item">
            <span class="filter-label">付款日期</span>
            <el-date-picker v-model="paymentDate" size="small" type="daterange" range-separator="至" start-placeholder="开始" end-placeholder="结束" class="filter-date"></el-date-picker>
          </div>
          <div class="filter-item">
            <span class="filter-label">店铺</span>
            <el-select v-model="params.shopName" size="small" filterable clearable placeholder="全部">
              <el-option v-for="item in shopOptions" :key="item" :label="item" :value="item"></el-option>
            </el-select>
          </div>
          <div class="filter-item">
            <span class="filter-label">供应商</span>
            <el-select v-model="params.supplierId" size="small" filterable clearable placeholder="全部">
              <el-option v-for="supplier in supplierList" :key="supplier.supplierId" :label="supplier.supplierShortName" :value="supplier.supplierId"></el-option>
            </el-select>
          </div>
          <div class="filter-item">
            <span class="filter-label">产品类型</span>
            <el-select v-model="params.productType" size="small" filterable clearable placeholder="全部">
              <el-option v-for="product in productTypeList" :key="product.productType" :label="product.productName" :value="product.productType"></el-option>
            </el-select>
          </div>
          <div class="filter-item">
            <span class="filter-label">业务类型</span>
            <el-select v-model="params.sourceType" size="small" clearable placeholder="全部">
              <el-option v-for="item in sourceTypeOptions" :key="item.value || 'all'" :label="item.label" :value="item.value"></el-option>
            </el-select>
          </div>
          <div class="filter-item">
            <span class="filter-label">支付订单编号</span>
            <el-input v-model="params.paymentGroupId" size="small" clearable placeholder="请输入支付订单编号"></el-input>
          </div>
          <div class="filter-item">
            <span class="filter-label">订单状态</span>
            <el-select v-model="params.filterOrderStatus" size="small" clearable placeholder="全部">
              <el-option v-for="item in orderStatusOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
          </div>
          <div class="filter-item">
            <span class="filter-label">开票状态</span>
            <el-select v-model="params.invoiceStatus" size="small" clearable placeholder="全部" class="filter-select-wide">
              <el-option v-for="item in invoiceStatusOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
            <el-button type="primary" size="small" icon="el-icon-search" class="query-btn" @click="queryBtn">查询</el-button>
          </div>
        </div>
      </div>
    </div>

    <div class="table-panel" ref="tablePanel">
      <el-tabs v-model="activeStatusTab" class="status-tabs" @tab-click="onStatusTabChange">
        <el-tab-pane v-for="tab in statusTabs" :key="tab.name" :name="tab.name">
          <span slot="label" class="status-tab-label">
            {{ tab.label }}
            <el-badge v-if="tab.showBadge && statusCounts[tab.name] > 0" :value="statusCounts[tab.name]" class="status-tab-badge"></el-badge>
          </span>
        </el-tab-pane>
      </el-tabs>

      <div v-if="isPaymentCompletedTab" class="page-toolbar" ref="invoiceToolbar">
        <el-button type="primary" size="small" icon="el-icon-document" :disabled="!selectedPayOrders.length" @click="onGenerateInvoice"> 生成开票记录 </el-button>
        <span v-if="selectedPayOrders.length" class="toolbar-tip">已选 {{ selectedPayOrders.length }} 单</span>
      </div>

      <div class="table-container">
        <el-table ref="payOrderTable" :data="tableData" border stripe size="mini" :height="screenHeight" v-loading="loading" element-loading-text="加载中..." :row-key="getPayOrderTableRowKey" :span-method="spanMethod" show-summary :summary-method="getTableSummaries" cell-class-name="tableCellClassName" header-cell-class-name="table-header-cell" class="pay-order-table mgmt-table" @selection-change="handlePayOrderSelectionChange">
          <el-table-column v-if="isPaymentCompletedTab" type="selection" width="42" align="center" fixed="left" :selectable="payOrderRowSelectable"></el-table-column>
          <el-table-column label="序号" width="48" align="center" fixed="left">
            <template slot-scope="scope">
              <span v-if="scope.row._paymentRowSpan > 0">{{ getOrderRowIndex(scope.row) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="paymentGroupId" label="支付订单编号" width="120" align="center" class-name="merge-col" show-overflow-tooltip></el-table-column>
          <el-table-column prop="sourceType" label="业务类型" width="72" align="center" class-name="merge-col">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.sourceTypeLabel || scope.row.sourceType" size="mini" :type="getSourceTypeTagType(scope.row.sourceType)" effect="plain">
                {{ scope.row.sourceTypeLabel || getSourceTypeLabel(scope.row.sourceType) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column prop="shopName" label="店铺" width="50" align="center" class-name="merge-col" show-overflow-tooltip></el-table-column>
          <el-table-column prop="styleName" label="产品名称" width="130" show-overflow-tooltip></el-table-column>

          <el-table-column prop="supplierName" label="采购供应商" width="90" class-name="merge-col" show-overflow-tooltip></el-table-column>
          <el-table-column prop="unitPrice" label="单价" width="60" align="right" class-name="amount-col">
            <template slot-scope="scope">
              <span v-if="scope.row.productType" class="price-history-link" @click="onViewProductPriceHistory(scope.row)">{{ formatNumber(scope.row.unitPrice) }}</span>
              <span v-else>{{ formatNumber(scope.row.unitPrice) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="quantity" label="数量" width="60" align="right" class-name="amount-col"></el-table-column>
          <el-table-column prop="totalPrice" label="总价" width="80" align="right" class-name="amount-col">
            <template slot-scope="scope">{{ formatNumber(scope.row.totalPrice) }}</template>
          </el-table-column>
          <el-table-column prop="taxRate" label="税率" width="50" align="right">
            <template slot-scope="scope">{{ formatTaxRate(scope.row.taxRate) }}</template>
          </el-table-column>
          <el-table-column prop="taxAmount" label="税额" width="65" align="right" class-name="amount-col">
            <template slot-scope="scope">{{ formatNumber(scope.row.taxAmount) }}</template>
          </el-table-column>
          <el-table-column prop="freight" label="运费" width="60" align="right" class-name="merge-col amount-col">
            <template slot-scope="scope">{{ formatNumber(scope.row.freight) }}</template>
          </el-table-column>
          <el-table-column prop="platformDiscount" label="平台优惠" width="60" align="right" class-name="merge-col amount-col">
            <template slot-scope="scope">{{ formatNumber(scope.row.platformDiscount) }}</template>
          </el-table-column>
          <el-table-column prop="returnExchangeFee" label="退换货费" width="70" align="right" class-name="merge-col amount-col">
            <template slot-scope="scope">{{ formatNumber(scope.row.returnExchangeFee) }}</template>
          </el-table-column>
          <el-table-column prop="otherFee" label="其他费用" width="60" align="right" class-name="merge-col amount-col">
            <template slot-scope="scope">{{ formatNumber(scope.row.otherFee) }}</template>
          </el-table-column>
          <el-table-column prop="payableAmount" label="应付金额" width="80" align="right" class-name="merge-col amount-col highlight-amount">
            <template slot-scope="scope">
              <span class="money-text">{{ formatNumber(scope.row.payableAmount) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="actualPaidAmount" label="实付金额" width="80" align="right" class-name="merge-col amount-col">
            <template slot-scope="scope">
              <span class="money-text primary">{{ formatNumber(scope.row.actualPaidAmount) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="amountDiff" label="差额" width="60" align="right" class-name="merge-col amount-col">
            <template slot-scope="scope">
              <span class="money-text" :class="getAmountDiffClass(scope.row.amountDiff)">{{ formatAmountDiff(scope.row.amountDiff) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="orderPlatform" label="下单平台" width="60" class-name="merge-col">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.orderPlatform" size="mini" :type="scope.row.orderPlatform === '对公' ? 'warning' : 'info'" effect="plain">
                {{ scope.row.orderPlatform }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="orderStatus" label="订单状态" width="80" class-name="merge-col">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.orderStatusLabel || scope.row.orderStatus" size="mini" :type="getOrderStatusType(scope.row.orderStatus)" effect="light">
                {{ scope.row.orderStatusLabel || scope.row.orderStatus }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="invoiceStatus" label="开票状态" width="88" align="center" class-name="merge-col">
            <template slot-scope="scope">
              <template v-if="scope.row.orderStatus === PAY_ORDER_STATUS.PAYMENT_COMPLETED">
                <el-tag v-if="scope.row.invoiceStatusLabel" size="mini" :type="getInvoiceStatusType(scope.row.invoiceStatus)" effect="light">
                  {{ scope.row.invoiceStatusLabel }}
                </el-tag>
                <span v-else class="proof-empty">未开票</span>
              </template>
              <span v-else class="proof-empty">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="orderConfirmProof" label="订单确认证明" width="120" class-name="merge-col">
            <template slot-scope="scope">
              <div v-if="getProofImageList(scope.row, 'orderConfirmProof').length" class="proof-thumb-list">
                <el-image v-for="(url, index) in getProofImageList(scope.row, 'orderConfirmProof')" :key="url + '-' + index" :src="url" :preview-src-list="getProofImageList(scope.row, 'orderConfirmProof')" fit="cover" class="proof-thumb"></el-image>
              </div>
              <span v-else class="proof-empty">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="paymentScreenshot" label="支付凭证" width="120" class-name="merge-col">
            <template slot-scope="scope">
              <div v-if="getProofImageList(scope.row, 'paymentScreenshot').length" class="proof-thumb-list">
                <el-image v-for="(url, index) in getProofImageList(scope.row, 'paymentScreenshot')" :key="url + '-' + index" :src="url" :preview-src-list="getProofImageList(scope.row, 'paymentScreenshot')" fit="cover" class="proof-thumb"></el-image>
              </div>
              <span v-else class="proof-empty">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="remark" label="备注" min-width="100" class-name="merge-col">
            <template slot-scope="scope">
              <div v-if="scope.row.remark || scope.row.rejectReason" class="remark-cell">
                <div v-if="scope.row.remark">{{ scope.row.remark }}</div>
                <div v-if="scope.row.rejectReason" class="reject-reason-text">打回原因：{{ scope.row.rejectReason }}</div>
              </div>
              <span v-else class="proof-empty">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="createTime" label="创建时间" width="96" align="center" class-name="merge-col">
            <template slot-scope="scope">
              <div class="row-item">{{ formatCreateDate(scope.row.createTime) }}</div>
              <div class="row-item row-sub">{{ formatCreateTimePart(scope.row.createTime) }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="paymentTime" label="付款时间" width="96" align="center" class-name="merge-col">
            <template slot-scope="scope">
              <template v-if="scope.row.paymentTime">
                <div class="row-item">{{ formatCreateDate(scope.row.paymentTime) }}</div>
                <div class="row-item row-sub">{{ formatCreateTimePart(scope.row.paymentTime) }}</div>
              </template>
              <span v-else class="proof-empty">-</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="180" fixed="right" align="center" class-name="action-col">
            <template slot-scope="scope">
              <div v-if="scope.row._paymentRowSpan > 0" class="action-cell">
                <el-button v-for="action in getVisiblePayOrderActions(scope.row)" :key="action.key" type="text" size="small" :class="['action-btn', action.btnClass]" @click="handlePayOrderRowAction(action.key, scope.row)">
                  {{ action.label }}
                </el-button>
                <el-dropdown v-if="getMorePayOrderActions(scope.row).length" trigger="click" @command="cmd => handlePayOrderRowAction(cmd, scope.row)">
                  <el-button type="text" size="small" class="action-btn"> 更多<i class="el-icon-arrow-down el-icon--right"></i> </el-button>
                  <el-dropdown-menu slot="dropdown" class="pay-order-action-dropdown">
                    <el-dropdown-item v-for="action in getMorePayOrderActions(scope.row)" :key="action.key" :command="action.key" :class="action.btnClass">
                      {{ action.label }}
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="pagination-box">
        <el-pagination layout="prev, pager, next, total" background :page-size="params.pageSize" :total="total" :current-page="currentPage" @current-change="handlerPageChange"></el-pagination>
      </div>
    </div>

    <el-dialog :title="priceHistoryTitle" :visible.sync="priceHistoryVisible" width="1331px" top="8vh" custom-class="pay-order-price-history-dialog">
      <el-table :data="priceHistoryList" border stripe size="mini" max-height="460" class="record-table" v-loading="priceHistoryLoading" :row-class-name="priceHistoryRowClassName">
        <el-table-column prop="paymentGroupId" label="支付订单编号" width="130" align="center" show-overflow-tooltip></el-table-column>
        <el-table-column prop="shopName" label="店铺" width="70" show-overflow-tooltip></el-table-column>
        <el-table-column prop="productName" label="产品名称" width="160" show-overflow-tooltip></el-table-column>
        <el-table-column prop="supplierName" label="采购供应商" width="100" show-overflow-tooltip></el-table-column>
        <el-table-column prop="unitPrice" label="单价" width="70" align="right">
          <template slot-scope="scope">{{ formatNumber(scope.row.unitPrice) }}</template>
        </el-table-column>
        <el-table-column prop="quantity" label="数量" width="60" align="right"></el-table-column>
        <el-table-column prop="totalPrice" label="总价" width="80" align="right">
          <template slot-scope="scope">{{ formatNumber(scope.row.totalPrice) }}</template>
        </el-table-column>
        <el-table-column prop="taxRate" label="税率" width="60" align="right">
          <template slot-scope="scope">{{ formatTaxRate(scope.row.taxRate) }}</template>
        </el-table-column>
        <el-table-column prop="taxAmount" label="税额" width="70" align="right">
          <template slot-scope="scope">{{ formatNumber(scope.row.taxAmount) }}</template>
        </el-table-column>
        <el-table-column prop="freight" label="运费" width="70" align="right">
          <template slot-scope="scope">{{ formatNumber(scope.row.freight) }}</template>
        </el-table-column>
        <el-table-column prop="platformDiscount" label="平台优惠" width="80" align="right">
          <template slot-scope="scope">{{ formatNumber(scope.row.platformDiscount) }}</template>
        </el-table-column>
        <el-table-column prop="returnExchangeFee" label="退换货费" width="80" align="right">
          <template slot-scope="scope">{{ formatNumber(scope.row.returnExchangeFee) }}</template>
        </el-table-column>
        <el-table-column prop="otherFee" label="其他费用" width="80" align="right">
          <template slot-scope="scope">{{ formatNumber(scope.row.otherFee) }}</template>
        </el-table-column>
        <el-table-column prop="payableAmount" label="应付金额" width="90" align="right">
          <template slot-scope="scope">{{ formatNumber(scope.row.payableAmount) }}</template>
        </el-table-column>
        <el-table-column prop="actualPaidAmount" label="实付金额" width="90" align="right">
          <template slot-scope="scope">{{ formatNumber(scope.row.actualPaidAmount) }}</template>
        </el-table-column>
      </el-table>
      <div class="price-history-pagination">
        <el-pagination layout="prev, pager, next, total" background :page-size="priceHistoryPageSize" :total="priceHistoryTotal" :current-page="priceHistoryPage" @current-change="onPriceHistoryPageChange"></el-pagination>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="priceHistoryVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <el-dialog title="采购记录" :visible.sync="purchaseRecordVisible" width="920px" custom-class="pay-order-record-dialog">
      <el-table :data="purchaseRecordList" border stripe size="mini" max-height="420" class="record-table" show-summary :summary-method="getPurchaseRecordSummaries">
        <el-table-column label="序号" width="50" align="center">
          <template slot-scope="scope">{{ scope.$index + 1 }}</template>
        </el-table-column>
        <el-table-column prop="date" label="采购日期" width="90" align="center"></el-table-column>
        <el-table-column prop="shopName" label="店铺" width="70"></el-table-column>
        <el-table-column prop="sku" label="SKU" min-width="140" show-overflow-tooltip></el-table-column>
        <el-table-column prop="productName" label="产品名称" width="110" show-overflow-tooltip></el-table-column>
        <el-table-column prop="purchaseColorName" label="采购颜色" width="88"></el-table-column>
        <el-table-column prop="model" label="机型" width="80"></el-table-column>
        <el-table-column prop="planPurchase" label="采购数量" width="80" align="right"></el-table-column>
      </el-table>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="purchaseRecordVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <el-dialog title="采购材料" :visible.sync="purchaseMaterialVisible" width="920px" custom-class="pay-order-record-dialog">
      <el-table :data="purchaseMaterialList" border stripe size="mini" max-height="420" class="record-table" show-summary :summary-method="getPurchaseMaterialSummaries" v-loading="purchaseMaterialLoading">
        <el-table-column label="序号" width="50" align="center">
          <template slot-scope="scope">{{ scope.$index + 1 }}</template>
        </el-table-column>
        <el-table-column prop="materialName" label="材料名称" min-width="140" show-overflow-tooltip></el-table-column>
        <el-table-column prop="purchaseColorName" label="采购颜色" width="100" show-overflow-tooltip></el-table-column>
        <el-table-column prop="boxSpec" label="规格" min-width="160" show-overflow-tooltip></el-table-column>
        <el-table-column prop="unitPrice" label="单价" width="80" align="right">
          <template slot-scope="scope">{{ formatNumber(scope.row.unitPrice) }}</template>
        </el-table-column>
        <el-table-column prop="quantity" label="数量" width="80" align="right"></el-table-column>
        <el-table-column prop="totalAmount" label="金额" width="88" align="right">
          <template slot-scope="scope">{{ formatNumber(scope.row.totalAmount) }}</template>
        </el-table-column>
      </el-table>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="purchaseMaterialVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <el-dialog title="操作记录" :visible.sync="opreateLogVisible" width="1200px">
      <el-table :data="operateHistory" border stripe size="mini" max-height="420" class="record-table">
        <el-table-column label="序号" width="50" align="center">
          <template slot-scope="scope">{{ scope.$index + 1 }}</template>
        </el-table-column>
        <el-table-column prop="opreateUserName" label="操作人" width="90"></el-table-column>
        <el-table-column prop="opreateTimeLabel" label="操作时间" width="170"></el-table-column>
        <el-table-column prop="opreateTypeLabel" label="操作类型" width="120"></el-table-column>
        <el-table-column prop="bussinessId" label="业务ID" width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="content" label="操作详情" min-width="240" show-overflow-tooltip></el-table-column>
      </el-table>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="opreateLogVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <el-dialog title="编辑支付订单" :visible.sync="editDialogVisible" width="920px" custom-class="pay-order-edit-dialog" :close-on-click-modal="false">
      <div class="pay-order-section">
        <div class="section-title">产品明细</div>
        <el-table :data="payOrderPreviewList" border stripe size="mini" show-summary :summary-method="getPayOrderSummaries" v-loading="payOrderPreviewLoading" max-height="320">
          <el-table-column label="序号" width="50" align="center" prop="index"></el-table-column>
          <el-table-column prop="productName" label="产品名称" width="150" show-overflow-tooltip></el-table-column>
          <el-table-column prop="purchaseQuantity" label="采购数量" width="80" align="right"></el-table-column>
          <el-table-column prop="purchaseAmount" label="采购金额" width="90" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.purchaseAmount) }}</template>
          </el-table-column>
          <el-table-column prop="taxRate" label="税率" width="70" align="right">
            <template slot-scope="scope">{{ formatPayOrderTaxRate(scope.row.taxRate) }}</template>
          </el-table-column>
          <el-table-column prop="taxAmount" label="税费" width="80" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.taxAmount) }}</template>
          </el-table-column>
          <el-table-column prop="payableAmount" label="应付金额" width="90" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.payableAmount) }}</template>
          </el-table-column>
        </el-table>
      </div>
      <div class="pay-order-form">
        <div class="section-title">支付信息</div>
        <div class="form-grid">
          <div class="form-item wide">
            <span class="form-label">采购供应商</span>
            <el-select v-model="payOrderForm.supplierId" size="small" filterable placeholder="请选择供应商" @change="onPayOrderSupplierChange">
              <el-option v-for="supplier in supplierList" :key="supplier.supplierId" :label="supplier.supplierShortName" :value="supplier.supplierId"></el-option>
            </el-select>
            <span class="tax-label">税率：{{ formatPayOrderTaxRate(payOrderSupplierTaxRate) }}</span>
          </div>
          <div class="form-item wide">
            <span class="form-label">运费</span>
            <el-input v-model.number="payOrderForm.freight" size="small" type="number" @input="calcPayOrderTotal"></el-input>
          </div>
          <div class="form-item wide">
            <span class="form-label">平台优惠</span>
            <el-input v-model.number="payOrderForm.platformDiscount" size="small" type="number" @input="calcPayOrderTotal"></el-input>
          </div>
          <div class="form-item wide">
            <span class="form-label">退换货费</span>
            <el-input v-model.number="payOrderForm.returnExchangeFee" size="small" type="number" @input="calcPayOrderTotal"></el-input>
          </div>
          <div class="form-item wide">
            <span class="form-label">其他费用</span>
            <el-input v-model.number="payOrderForm.otherFee" size="small" type="number" @input="calcPayOrderTotal"></el-input>
          </div>
          <div class="form-item wide">
            <span class="form-label">下单平台</span>
            <el-radio-group v-model="payOrderForm.orderPlatform" size="small">
              <el-radio label="对公">对公</el-radio>
              <el-radio label="平台">平台</el-radio>
            </el-radio-group>
          </div>
        </div>
        <div class="amount-summary">
          <div class="amount-row">
            <span class="amount-label">应付金额</span>
            <span class="amount-value payable">{{ formatMoney(payOrderForm.payableAmount) }}</span>
          </div>
          <div class="amount-row">
            <span class="amount-label">实付金额</span>
            <el-input v-model.number="payOrderForm.actualPaidAmount" size="small" type="number" class="amount-input" @input="onPayOrderActualPaidInput"></el-input>
          </div>
          <div class="amount-row">
            <span class="amount-label">差额</span>
            <span class="amount-value" :class="payOrderAmountDiffClass">{{ formatPayOrderAmountDiff(payOrderAmountDiff) }}</span>
            <span class="amount-tip">（实付金额 - 应付金额）</span>
          </div>
        </div>
        <div class="form-item wide remark-item">
          <span class="form-label">备注</span>
          <el-input v-model="payOrderForm.remark" type="textarea" :rows="3" size="small" placeholder="请输入备注"></el-input>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="editDialogVisible = false">取 消</el-button>
        <el-button type="danger" plain @click="onVoidGroupInDialog">作废整单</el-button>
        <el-button type="primary" @click="saveEditGroup" :loading="payOrderSubmitLoading">保 存</el-button>
      </div>
    </el-dialog>

    <el-dialog title="打回" :visible.sync="rejectDialogVisible" width="480px" custom-class="pay-order-reject-dialog" :close-on-click-modal="false">
      <div class="reject-dialog-tip">确认打回到「{{ rejectTargetStatusLabel }}」</div>
      <el-input v-model="rejectReason" type="textarea" :rows="4" maxlength="500" show-word-limit placeholder="请输入打回原因"></el-input>
      <div slot="footer" class="dialog-footer">
        <el-button @click="rejectDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitRejectGroup" :loading="rejectSubmitting">确 定</el-button>
      </div>
    </el-dialog>

    <el-dialog :title="proofUploadTitle" :visible.sync="proofUploadVisible" width="640px" custom-class="pay-order-proof-dialog" :close-on-click-modal="false" @opened="onProofUploadDialogOpened" @closed="onProofUploadDialogClosed">
      <div ref="proofPastePanel" class="proof-upload-panel" tabindex="0" v-loading="proofUploading">
        <div class="proof-upload-list">
          <div v-for="(url, index) in proofUploadUrls" :key="url + '-' + index" class="proof-upload-item">
            <el-image :src="url" fit="cover" class="proof-upload-image"></el-image>
            <i class="el-icon-error proof-upload-remove" @click="removeProofUploadImage(index)"></i>
          </div>
          <el-upload class="proof-upload-add" action="/biz/oss/upload" name="file" accept="image/*" :show-file-list="false" :before-upload="beforeProofImageUpload" :on-success="handleProofUploadSuccess" :disabled="proofUploading">
            <div class="proof-upload-trigger">
              <i class="el-icon-plus avatar-uploader-icon"></i>
              <span class="proof-upload-text">添加图片</span>
            </div>
          </el-upload>
        </div>
        <div class="proof-upload-tip">支持 JPG、PNG、GIF、WEBP、BMP；可上传多张，可在本弹窗内 Ctrl+V 粘贴截图</div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="proofUploadVisible = false">取 消</el-button>
        <el-button type="primary" @click="saveProofUpload" :loading="proofUploadSaving">保 存</el-button>
      </div>
    </el-dialog>

    <el-dialog title="开票信息预览" :visible.sync="invoicePreviewVisible" width="960px" custom-class="pay-order-invoice-dialog" :close-on-click-modal="false">
      <div class="invoice-preview-config">
        <div class="invoice-preview-config-row">
          <span class="invoice-preview-config-label">采购供应商</span>
          <span class="invoice-preview-config-value">{{ invoicePreviewForm.supplierName || "-" }}</span>
        </div>
        <div class="invoice-preview-config-row">
          <span class="invoice-preview-config-label">开票信息</span>
          <el-select v-model="invoicePreviewForm.invoiceInfoId" size="small" filterable clearable placeholder="请选择开票信息" class="invoice-preview-config-select" :disabled="!invoicePreviewSupplierInvoiceInfoList.length">
            <el-option v-for="(item, index) in invoicePreviewSupplierInvoiceInfoList" :key="item.invoiceInfoId" :label="getInvoiceInfoOptionLabel(item, index)" :value="item.invoiceInfoId"></el-option>
          </el-select>
        </div>
        <div v-if="!invoicePreviewSupplierInvoiceInfoList.length" class="invoice-preview-config-tip">该供应商暂未配置开票信息，请先在采购供应商管理中维护</div>
        <el-descriptions v-else-if="selectedInvoicePreviewInfo" :column="2" border size="small" class="invoice-preview-info-descriptions">
          <el-descriptions-item label="单位名称">{{ displayInvoicePreviewField(selectedInvoicePreviewInfo.invoiceCompanyName) }}</el-descriptions-item>
          <el-descriptions-item label="税号">{{ displayInvoicePreviewField(selectedInvoicePreviewInfo.invoiceTaxNo) }}</el-descriptions-item>
          <el-descriptions-item label="单位地址">{{ displayInvoicePreviewField(selectedInvoicePreviewInfo.invoiceCompanyAddress) }}</el-descriptions-item>
          <el-descriptions-item label="电话">{{ displayInvoicePreviewField(selectedInvoicePreviewInfo.invoicePhone) }}</el-descriptions-item>
          <el-descriptions-item label="开户银行">{{ displayInvoicePreviewField(selectedInvoicePreviewInfo.invoiceBankName) }}</el-descriptions-item>
          <el-descriptions-item label="银行账户">{{ displayInvoicePreviewField(selectedInvoicePreviewInfo.invoiceBankAccount) }}</el-descriptions-item>
          <el-descriptions-item label="收票邮箱" :span="2">{{ displayInvoicePreviewField(selectedInvoicePreviewInfo.invoiceEmail) }}</el-descriptions-item>
        </el-descriptions>
      </div>
      <div class="invoice-preview-panel">
        <el-table :data="invoicePreviewLines" border stripe size="mini" max-height="420" show-summary :summary-method="getInvoicePreviewSummaries" v-loading="invoicePreviewLoading" class="invoice-preview-table" empty-text="暂无开票明细">
          <el-table-column prop="projectName" label="项目名称" min-width="160" show-overflow-tooltip></el-table-column>
          <el-table-column prop="specModel" label="规格型号" min-width="140" show-overflow-tooltip></el-table-column>
          <el-table-column prop="unit" label="单位" width="64" align="center">
            <template slot-scope="scope">{{ scope.row.unit || "个" }}</template>
          </el-table-column>
          <el-table-column prop="quantity" label="数量" width="80" align="right"></el-table-column>
          <el-table-column prop="unitPrice" label="单价" width="110" align="right">
            <template slot-scope="scope">{{ formatUnitPrice(scope.row.unitPrice) }}</template>
          </el-table-column>
          <el-table-column prop="amount" label="金额" width="96" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.amount) }}</template>
          </el-table-column>
          <el-table-column prop="taxRate" label="税率" width="80" align="right">
            <template slot-scope="scope">{{ formatTaxRate(scope.row.taxRate) }}</template>
          </el-table-column>
          <el-table-column prop="taxAmount" label="税额" width="96" align="right">
            <template slot-scope="scope">{{ formatMoney(scope.row.taxAmount) }}</template>
          </el-table-column>
        </el-table>
        <div class="invoice-preview-footer">
          <span class="invoice-preview-footer-label">开票金额</span>
          <span class="invoice-preview-footer-value">{{ formatMoney(invoicePreviewInvoiceAmount) }}</span>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="invoicePreviewVisible = false">取 消</el-button>
        <el-button type="primary" @click="saveInvoicePreview" :loading="invoicePreviewSaving">确认生成</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { payOrderModules, invoiceModules, requestModules, ossModules } from "@r";
import { PAY_ORDER_STATUS, PAY_ORDER_STATUS_FILTER_OPTIONS, PAY_ORDER_STATUS_TABS, PAY_ORDER_REVIEWABLE_STATUSES, PAY_ORDER_STATUS_PASS_BUTTON_LABEL, PAY_ORDER_STATUS_REJECT_TARGET, getPayOrderStatusTagType, PAY_ORDER_STATUS_LABEL, PAY_ORDER_SOURCE_TYPE, PAY_ORDER_SOURCE_TYPE_FILTER_OPTIONS, getPayOrderSourceTypeLabel, getPayOrderSourceCapabilities, normalizePayOrderSourceType } from "../../constants/payOrderStatus";
import { collectShopTypesFromPayOrderRow } from "../../utils/shopIdentity";

const SHOP_OPTIONS = ["灵柚", "萧发", "誉腾", "甄昂", "众联", "杭傲"];

const PAYMENT_MERGE_PROPS = ["shopName", "shopType", "paymentGroupId", "sourceType", "sourceTypeLabel", "createTime", "paymentTime", "supplierName", "freight", "platformDiscount", "returnExchangeFee", "otherFee", "payableAmount", "actualPaidAmount", "amountDiff", "orderPlatform", "orderStatus", "orderStatusLabel", "invoiceStatus", "orderConfirmProof", "paymentScreenshot", "remark"];

const INVOICE_STATUS = {
  PENDING: 0,
  INVOICED: 1,
  VOID: 2,
  INFO_REVIEW: 3,
  INVOICE_REVIEW: 4,
  INFO_CONFIRM: 5,
};

const INVOICE_STATUS_FILTER_OPTIONS = [
  { label: "未开票", value: "未开票" },
  { label: "开票信息待确认", value: "开票信息待确认" },
  { label: "待开票信息审核", value: "待开票信息审核" },
  { label: "待开票", value: "待开票" },
  { label: "待发票审核", value: "待发票审核" },
  { label: "已开票", value: "已开票" },
];

export default {
  data() {
    return {
      tableData: [],
      total: 0,
      currentPage: 1,
      loading: false,
      activeStatusTab: "all",
      statusTabs: PAY_ORDER_STATUS_TABS,
      PAY_ORDER_STATUS,
      statusCounts: {
        [PAY_ORDER_STATUS.PENDING_ORDER]: 0,
        [PAY_ORDER_STATUS.PENDING_PURCHASE_AUDIT]: 0,
        [PAY_ORDER_STATUS.PENDING_PAYMENT]: 0,
        [PAY_ORDER_STATUS.PENDING_PAYMENT_AUDIT]: 0,
      },
      editDialogVisible: false,
      payOrderPreviewLoading: false,
      payOrderSubmitLoading: false,
      payOrderPreviewList: [],
      payOrderPreviewShouldSyncActualPaid: false,
      payOrderForm: {
        paymentGroupId: "",
        supplierId: "",
        freight: 0,
        platformDiscount: 0,
        returnExchangeFee: 0,
        otherFee: 0,
        orderPlatform: "对公",
        actualPaidAmount: 0,
        payableAmount: 0,
        orderStatus: PAY_ORDER_STATUS.PENDING_ORDER,
        orderConfirmProof: [],
        paymentScreenshot: [],
        remark: "",
      },
      supplierList: [],
      productTypeList: [],
      shopOptions: SHOP_OPTIONS,
      createDate: [],
      paymentDate: [],
      orderStatusOptions: PAY_ORDER_STATUS_FILTER_OPTIONS,
      invoiceStatusOptions: INVOICE_STATUS_FILTER_OPTIONS,
      sourceTypeOptions: PAY_ORDER_SOURCE_TYPE_FILTER_OPTIONS,
      params: {
        beginIndex: 0,
        pageSize: 50,
        shopName: "",
        supplierId: "",
        productType: "",
        filterOrderStatus: "",
        invoiceStatus: "",
        paymentGroupId: "",
        sourceType: "",
      },
      purchaseRecordVisible: false,
      purchaseRecordList: [],
      priceHistoryVisible: false,
      priceHistoryLoading: false,
      priceHistoryList: [],
      priceHistoryTotal: 0,
      priceHistoryPage: 1,
      priceHistoryPageSize: 20,
      priceHistoryProductType: 0,
      priceHistoryProductName: "",
      priceHistoryPaymentGroupId: "",
      purchaseMaterialVisible: false,
      purchaseMaterialLoading: false,
      purchaseMaterialList: [],
      opreateLogVisible: false,
      operateHistory: [],
      proofUploadVisible: false,
      proofUploadTitle: "",
      proofUploadUrls: [],
      proofUploadField: "",
      proofUploadRow: null,
      proofUploadSaving: false,
      proofUploading: false,
      rejectDialogVisible: false,
      rejectSubmitting: false,
      rejectPaymentGroupId: "",
      rejectTargetStatus: "",
      rejectReason: "",
      screenHeight: 400,
      selectedPayOrders: [],
      invoicePreviewVisible: false,
      invoicePreviewLoading: false,
      invoicePreviewSaving: false,
      invoicePreviewLines: [],
      invoicePreviewForm: {
        supplierId: "",
        supplierName: "",
        paymentGroupIds: [],
        invoiceInfoId: "",
        invoiceTaxRate: 0,
        invoiceAmount: 0,
        supplierInvoiceInfoList: [],
      },
    };
  },
  computed: {
    isPaymentCompletedTab() {
      return this.activeStatusTab === PAY_ORDER_STATUS.PAYMENT_COMPLETED;
    },
    invoicePreviewInvoiceAmount() {
      let totalAmount = this.invoicePreviewLines.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
      let totalTaxAmount = this.invoicePreviewLines.reduce((sum, row) => sum + (parseFloat(row.taxAmount) || 0), 0);
      return parseFloat((totalAmount + totalTaxAmount).toFixed(2));
    },
    payOrderAmountDiff() {
      let actualPaidAmount = parseFloat(this.payOrderForm.actualPaidAmount) || 0;
      let payableAmount = parseFloat(this.payOrderForm.payableAmount) || 0;
      return parseFloat((actualPaidAmount - payableAmount).toFixed(2));
    },
    payOrderAmountDiffClass() {
      if (this.payOrderAmountDiff > 0) {
        return "positive";
      }
      if (this.payOrderAmountDiff < 0) {
        return "negative";
      }
      return "zero";
    },
    payOrderSupplierTaxRate() {
      let supplier = this.supplierList.find(item => item.supplierId == this.payOrderForm.supplierId);
      if (!supplier || supplier.invoiceTaxRate === undefined || supplier.invoiceTaxRate === null || supplier.invoiceTaxRate === "") {
        return 0;
      }
      let taxRate = parseFloat(supplier.invoiceTaxRate);
      return isNaN(taxRate) ? 0 : taxRate;
    },
    invoicePreviewSupplierInvoiceInfoList() {
      return this.invoicePreviewForm.supplierInvoiceInfoList || [];
    },
    selectedInvoicePreviewInfo() {
      let list = this.invoicePreviewSupplierInvoiceInfoList;
      if (!list.length) {
        return null;
      }
      let selected = list.find(item => item.invoiceInfoId === this.invoicePreviewForm.invoiceInfoId);
      return selected || list[0];
    },
    rejectTargetStatusLabel() {
      return PAY_ORDER_STATUS_LABEL[this.rejectTargetStatus] || this.rejectTargetStatus;
    },
    priceHistoryTitle() {
      if (this.priceHistoryProductName) {
        return "历史采购记录 · " + this.priceHistoryProductName;
      }
      return "历史采购记录";
    },
  },
  mounted() {
    this.fetchSuppliers();
    this.fetchProductTypes();
    this.fetchStatusCounts();
    this.getData();
    this.relayoutTable();
    this.$eventBus.$on("resize", this.relayoutTable);
  },
  beforeDestroy() {
    this.$eventBus.$off("resize", this.relayoutTable);
    document.removeEventListener("paste", this.handleProofPaste);
  },
  methods: {
    fetchSuppliers() {
      requestModules.fetchSuppliers({ beginIndex: 0, pageSize: 1000 }).then(res => {
        this.supplierList = res.ldata || [];
      });
    },
    fetchProductTypes() {
      requestModules.fetchParentProducts({ status: 1 }).then(res => {
        this.productTypeList = res.ldata || [];
        this.productTypeList.sort((a, b) => b.productType - a.productType);
      });
    },
    calculateHeight() {
      const panel = this.$refs.tablePanel;
      if (!panel) {
        return;
      }
      const tabs = panel.querySelector(".status-tabs");
      const pagination = panel.querySelector(".pagination-box");
      const toolbar = this.$refs.invoiceToolbar;
      const panelStyle = window.getComputedStyle(panel);
      const paddingTop = parseFloat(panelStyle.paddingTop) || 0;
      const paddingBottom = parseFloat(panelStyle.paddingBottom) || 0;
      const tabsHeight = tabs ? tabs.offsetHeight : 0;
      const paginationHeight = pagination ? pagination.offsetHeight : 0;
      const toolbarHeight = toolbar ? toolbar.offsetHeight + 8 : 0;
      const gap = 12;
      const availableHeight = panel.clientHeight - paddingTop - paddingBottom - tabsHeight - paginationHeight - toolbarHeight - gap;
      this.screenHeight = Math.max(availableHeight, 200);
    },
    relayoutTable() {
      this.$nextTick(() => {
        this.calculateHeight();
        this.$nextTick(() => {
          if (this.$refs.payOrderTable && this.$refs.payOrderTable.doLayout) {
            this.$refs.payOrderTable.doLayout();
          }
        });
      });
    },
    formatMoney(value) {
      if (value === "" || value === null || value === undefined) {
        return "0.00";
      }
      return parseFloat(value).toFixed(2);
    },
    formatUnitPrice(value) {
      if (value === "" || value === null || value === undefined) {
        return "0.00000";
      }
      return parseFloat(value).toFixed(5);
    },
    formatPayOrderTaxRate(value) {
      if (value === "" || value === null || value === undefined) {
        return "0%";
      }
      return (parseFloat(value) * 100).toFixed(0) + "%";
    },
    formatPayOrderAmountDiff(value) {
      if (value > 0) {
        return "+" + this.formatMoney(value);
      }
      return this.formatMoney(value);
    },
    getPayOrderSummaries(param) {
      const { columns, data } = param;
      const sums = [];
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = "合计";
          return;
        }
        if (column.property === "purchaseQuantity") {
          sums[index] = data.reduce((sum, row) => sum + (Number(row.purchaseQuantity) || 0), 0);
          return;
        }
        if (["purchaseAmount", "taxAmount", "payableAmount"].includes(column.property)) {
          sums[index] = this.formatMoney(data.reduce((sum, row) => sum + (Number(row[column.property]) || 0), 0));
          return;
        }
        sums[index] = "";
      });
      return sums;
    },
    getTableSummaries(param) {
      const { columns, data } = param;
      const groupFirstRows = data.filter(row => row._paymentRowSpan > 0);
      const sums = [];
      columns.forEach((column, index) => {
        if (column.type === "selection") {
          sums[index] = "";
          return;
        }
        if (column.label === "序号") {
          sums[index] = "合计";
          return;
        }
        if (column.property === "quantity") {
          sums[index] = data.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
          return;
        }
        if (["totalPrice", "taxAmount"].includes(column.property)) {
          sums[index] = this.formatNumber(data.reduce((sum, row) => sum + (parseFloat(row[column.property]) || 0), 0));
          return;
        }
        if (["freight", "platformDiscount", "returnExchangeFee", "otherFee", "payableAmount", "actualPaidAmount"].includes(column.property)) {
          sums[index] = this.formatNumber(groupFirstRows.reduce((sum, row) => sum + (parseFloat(row[column.property]) || 0), 0));
          return;
        }
        if (column.property === "amountDiff") {
          sums[index] = this.formatAmountDiff(groupFirstRows.reduce((sum, row) => sum + (parseFloat(row.amountDiff) || 0), 0));
          return;
        }
        sums[index] = "";
      });
      return sums;
    },
    getPurchaseRecordSummaries(param) {
      const { columns, data } = param;
      const sums = [];
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = "合计";
          return;
        }
        if (column.property === "planPurchase") {
          sums[index] = data.reduce((sum, row) => sum + (Number(row.planPurchase) || 0), 0);
          return;
        }
        sums[index] = "";
      });
      return sums;
    },
    getPurchaseMaterialSummaries(param) {
      const { columns, data } = param;
      const sums = [];
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = "合计";
          return;
        }
        if (column.property === "quantity") {
          sums[index] = data.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
          return;
        }
        if (column.property === "totalAmount") {
          sums[index] = this.formatNumber(data.reduce((sum, row) => sum + (parseFloat(row.totalAmount) || 0), 0));
          return;
        }
        sums[index] = "";
      });
      return sums;
    },
    applyPayOrderLineTaxRates(rows) {
      let taxRate = this.payOrderSupplierTaxRate;
      return (rows || []).map(row => {
        let purchaseAmount = parseFloat(row.purchaseAmount) || 0;
        let taxAmount = parseFloat((purchaseAmount * taxRate).toFixed(2));
        let payableAmount = parseFloat((purchaseAmount + taxAmount).toFixed(2));
        return {
          ...row,
          taxRate,
          taxAmount,
          payableAmount,
        };
      });
    },
    calcPayOrderTotal() {
      let linePayableAmount = this.payOrderPreviewList.reduce((sum, row) => sum + (Number(row.payableAmount) || 0), 0);
      let freight = parseFloat(this.payOrderForm.freight) || 0;
      let platformDiscount = parseFloat(this.payOrderForm.platformDiscount) || 0;
      let returnExchangeFee = parseFloat(this.payOrderForm.returnExchangeFee) || 0;
      let otherFee = parseFloat(this.payOrderForm.otherFee) || 0;
      this.payOrderForm.payableAmount = parseFloat((linePayableAmount + freight - platformDiscount + returnExchangeFee + otherFee).toFixed(2));
    },
    onPayOrderActualPaidInput() {
      if (this.payOrderForm.actualPaidAmount === "" || this.payOrderForm.actualPaidAmount === null) {
        this.payOrderForm.actualPaidAmount = 0;
      }
    },
    onPayOrderSupplierChange() {
      this.loadPayOrderPreview(true);
    },
    syncPayOrderActualPaidAmount() {
      if (this.payOrderPreviewShouldSyncActualPaid) {
        this.payOrderForm.actualPaidAmount = this.payOrderForm.payableAmount;
        this.payOrderPreviewShouldSyncActualPaid = false;
      }
    },
    buildPayOrderPreviewParams() {
      return {
        paymentGroupId: this.payOrderForm.paymentGroupId,
        supplierId: this.payOrderForm.supplierId || 0,
        freight: this.payOrderForm.freight,
        platformDiscount: this.payOrderForm.platformDiscount,
        returnExchangeFee: this.payOrderForm.returnExchangeFee,
        otherFee: this.payOrderForm.otherFee,
      };
    },
    loadPayOrderPreview(recalculateOnSupplierChange) {
      if (!this.payOrderForm.paymentGroupId) {
        return;
      }
      this.payOrderPreviewLoading = true;
      payOrderModules
        .previewPayOrderGroup(this.buildPayOrderPreviewParams())
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "加载支付订单失败");
            return;
          }
          let previewList = res.ldata || [];
          this.payOrderPreviewList = previewList;
          let mdata = res.mdata || {};
          this.payOrderForm.supplierId = mdata.supplierId || this.payOrderForm.supplierId;
          this.payOrderForm.freight = mdata.freight != null ? mdata.freight : this.payOrderForm.freight;
          this.payOrderForm.platformDiscount = mdata.platformDiscount != null ? mdata.platformDiscount : this.payOrderForm.platformDiscount;
          this.payOrderForm.returnExchangeFee = mdata.returnExchangeFee != null ? mdata.returnExchangeFee : this.payOrderForm.returnExchangeFee;
          this.payOrderForm.otherFee = mdata.otherFee != null ? mdata.otherFee : this.payOrderForm.otherFee;
          this.calcPayOrderTotal();
          if (recalculateOnSupplierChange) {
            this.payOrderForm.actualPaidAmount = this.payOrderForm.payableAmount;
          } else {
            this.syncPayOrderActualPaidAmount();
          }
        })
        .catch(() => {
          this.$message.error("加载支付订单失败");
        })
        .finally(() => {
          this.payOrderPreviewLoading = false;
        });
    },
    formatNumber(value) {
      if (value === "" || value === null || value === undefined) return "";
      return parseFloat(value).toFixed(2);
    },
    formatTaxRate(value) {
      if (value === "" || value === null || value === undefined) return "";
      return (parseFloat(value) * 100).toFixed(0) + "%";
    },
    displayInvoicePreviewField(value) {
      return value ? value : "-";
    },
    getInvoiceInfoOptionLabel(item, index) {
      let parts = [];
      if (item.invoiceCompanyName) {
        parts.push(item.invoiceCompanyName);
      }
      if (item.invoiceTaxNo) {
        parts.push("税号:" + item.invoiceTaxNo);
      }
      if (item.invoiceBankName) {
        parts.push(item.invoiceBankName);
      }
      if (item.invoiceEmail) {
        parts.push(item.invoiceEmail);
      }
      if (parts.length) {
        return "开票信息" + (index + 1) + " - " + parts.join(" | ");
      }
      return "开票信息" + (index + 1);
    },
    formatDisplayDate(date) {
      if (!date) return "";
      return String(date)
        .split("、")
        .map(item => item.replace(/-/g, ""))
        .join("、");
    },
    formatCreateDate(time) {
      if (!time) return "--";
      let date = new Date(Number(time));
      if (isNaN(date.getTime())) return "--";
      return date.format("yyyy-MM-dd");
    },
    formatCreateTimePart(time) {
      if (!time) return "--";
      let date = new Date(Number(time));
      if (isNaN(date.getTime())) return "--";
      return date.format("hh:mm:ss");
    },
    getOrderRowIndex(row) {
      let orderIndex = 0;
      for (let i = 0; i < this.tableData.length; i++) {
        if (this.tableData[i]._paymentRowSpan > 0) {
          orderIndex++;
          if (this.tableData[i].paymentGroupId === row.paymentGroupId) {
            return this.params.beginIndex + orderIndex;
          }
        }
      }
      return "";
    },
    getPayOrderTableRowKey(row) {
      return row._tableRowKey || row.paymentGroupId || "";
    },
    tableCellClassName({ row, column }) {
      let classNames = ["table-cell"];
      if (PAYMENT_MERGE_PROPS.includes(column.property)) {
        classNames.push("merge-col");
      }
      if (column.type === "selection" || column.label === "序号" || column.label === "操作") {
        classNames.push("merge-col");
      }
      return classNames.join(" ");
    },
    spanMethod({ row, column }) {
      if (PAYMENT_MERGE_PROPS.includes(column.property)) {
        let rowspan = row._paymentRowSpan || 0;
        return { rowspan, colspan: rowspan > 0 ? 1 : 0 };
      }
      if (column.type === "selection" || column.label === "序号" || column.label === "操作") {
        let rowspan = row._paymentRowSpan || 0;
        return { rowspan, colspan: rowspan > 0 ? 1 : 0 };
      }
      return { rowspan: 1, colspan: 1 };
    },
    formatAmountDiff(value) {
      let diff = parseFloat(value) || 0;
      if (diff > 0) {
        return "+" + this.formatNumber(diff);
      }
      return this.formatNumber(diff);
    },
    getAmountDiffClass(value) {
      let diff = parseFloat(value) || 0;
      if (diff > 0) {
        return "positive";
      }
      if (diff < 0) {
        return "negative";
      }
      return "zero";
    },
    getOrderStatusType(status) {
      return getPayOrderStatusTagType(status);
    },
    getSourceTypeLabel(sourceType) {
      return getPayOrderSourceTypeLabel(sourceType);
    },
    getSourceTypeTagType(sourceType) {
      if (sourceType === PAY_ORDER_SOURCE_TYPE.PURCHASE_ORDER) {
        return "success";
      }
      return "info";
    },
    getSourceCapabilities(row) {
      return (row && row.sourceCapabilities) || getPayOrderSourceCapabilities(row && row.sourceType);
    },
    getSourceRecordLabel(row) {
      return this.getSourceCapabilities(row).sourceRecordLabel || "采购记录";
    },
    getSourceMaterialLabel(row) {
      return this.getSourceCapabilities(row).sourceMaterialLabel || "采购材料";
    },
    getInvoiceStatusType(invoiceStatus) {
      if (invoiceStatus === INVOICE_STATUS.INFO_CONFIRM) {
        return "warning";
      }
      if (invoiceStatus === INVOICE_STATUS.INFO_REVIEW) {
        return "info";
      }
      if (invoiceStatus === INVOICE_STATUS.PENDING) {
        return "warning";
      }
      if (invoiceStatus === INVOICE_STATUS.INVOICE_REVIEW) {
        return "warning";
      }
      if (invoiceStatus === INVOICE_STATUS.INVOICED) {
        return "success";
      }
      if (invoiceStatus === INVOICE_STATUS.VOID) {
        return "info";
      }
      return "info";
    },
    getPassButtonLabel(orderStatus) {
      return PAY_ORDER_STATUS_PASS_BUTTON_LABEL[orderStatus] || "通过";
    },
    getPayOrderRowActions(row) {
      const actions = [];
      if (this.canReviewOrder(row)) {
        actions.push({ key: "approve", label: this.getPassButtonLabel(row.orderStatus), btnClass: "success" });
      }
      if (this.canRejectOrder(row)) {
        actions.push({ key: "reject", label: "打回", btnClass: "warning" });
      }
      if (this.canEditOrder(row)) {
        actions.push({ key: "edit", label: "编辑", btnClass: "" });
      }
      actions.push({ key: "purchaseRecords", label: this.getSourceRecordLabel(row), btnClass: "" });
      actions.push({ key: "purchaseMaterials", label: this.getSourceMaterialLabel(row), btnClass: "" });
      if (this.canManageOrderConfirmProof(row)) {
        actions.push({ key: "orderConfirmProof", label: this.getOrderConfirmProofButtonLabel(row), btnClass: "" });
      }
      if (this.canManagePaymentProof(row)) {
        actions.push({ key: "paymentScreenshot", label: this.getPaymentProofButtonLabel(row), btnClass: "" });
      }
      actions.push({ key: "operateLog", label: "操作记录", btnClass: "" });
      if (this.canVoidOrder(row)) {
        actions.push({ key: "void", label: "作废", btnClass: "danger" });
      }
      return actions;
    },
    getVisiblePayOrderActions(row) {
      return this.getPayOrderRowActions(row).slice(0, 2);
    },
    getMorePayOrderActions(row) {
      return this.getPayOrderRowActions(row).slice(2);
    },
    handlePayOrderRowAction(command, row) {
      if (command === "approve") {
        this.onApproveGroup(row);
      } else if (command === "reject") {
        this.onRejectGroup(row);
      } else if (command === "edit") {
        this.onEditGroup(row);
      } else if (command === "purchaseRecords") {
        this.onViewPurchaseRecords(row);
      } else if (command === "purchaseMaterials") {
        this.onViewPurchaseMaterials(row);
      } else if (command === "orderConfirmProof") {
        this.openProofUpload(row, "orderConfirmProof");
      } else if (command === "paymentScreenshot") {
        this.openProofUpload(row, "paymentScreenshot");
      } else if (command === "operateLog") {
        this.showOpreateLog(row);
      } else if (command === "void") {
        this.onVoidGroup(row);
      }
    },
    canReviewOrder(row) {
      return PAY_ORDER_REVIEWABLE_STATUSES.includes(row.orderStatus);
    },
    canRejectOrder(row) {
      return row.orderStatus === PAY_ORDER_STATUS.PENDING_PURCHASE_AUDIT || row.orderStatus === PAY_ORDER_STATUS.PENDING_PAYMENT || row.orderStatus === PAY_ORDER_STATUS.PENDING_PAYMENT_AUDIT;
    },
    canManageOrderConfirmProof(row) {
      return [PAY_ORDER_STATUS.PENDING_ORDER, PAY_ORDER_STATUS.PENDING_PURCHASE_AUDIT, PAY_ORDER_STATUS.PENDING_PAYMENT, PAY_ORDER_STATUS.PENDING_PAYMENT_AUDIT, PAY_ORDER_STATUS.PAYMENT_COMPLETED].includes(row.orderStatus);
    },
    canManagePaymentProof(row) {
      return row.orderStatus === PAY_ORDER_STATUS.PENDING_PAYMENT || row.orderStatus === PAY_ORDER_STATUS.PENDING_PAYMENT_AUDIT || row.orderStatus === PAY_ORDER_STATUS.PAYMENT_COMPLETED;
    },
    getOrderConfirmProofButtonLabel(row) {
      return this.getProofImageList(row, "orderConfirmProof").length ? "编辑订单确认证明" : "上传订单确认证明";
    },
    getPaymentProofButtonLabel(row) {
      return this.getProofImageList(row, "paymentScreenshot").length ? "编辑支付凭证" : "上传支付凭证";
    },
    normalizeProofImageList(value) {
      if (value == null || value === "") {
        return [];
      }
      if (Array.isArray(value)) {
        return value.map(item => String(item || "").trim()).filter(Boolean);
      }
      if (typeof value === "string") {
        let trimmed = value.trim();
        if (!trimmed) {
          return [];
        }
        if (trimmed.charAt(0) === "[") {
          try {
            let parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
              return parsed.map(item => String(item || "").trim()).filter(Boolean);
            }
          } catch (error) {
            return [trimmed];
          }
        }
        return [trimmed];
      }
      return [];
    },
    getProofImageList(row, field) {
      return this.normalizeProofImageList(row[field]);
    },
    openProofUpload(row, field) {
      let groupRow = row;
      if (row._paymentRowSpan <= 0) {
        groupRow = this.tableData.find(item => item.paymentGroupId === row.paymentGroupId && item._paymentRowSpan > 0) || row;
      }
      this.proofUploadRow = groupRow;
      this.proofUploadField = field;
      this.proofUploadUrls = [...this.getProofImageList(groupRow, field)];
      this.proofUploadTitle = field === "orderConfirmProof" ? this.getOrderConfirmProofButtonLabel(groupRow) : this.getPaymentProofButtonLabel(groupRow);
      this.proofUploadVisible = true;
    },
    onProofUploadDialogOpened() {
      document.addEventListener("paste", this.handleProofPaste);
      this.$nextTick(() => {
        if (this.$refs.proofPastePanel) {
          this.$refs.proofPastePanel.focus();
        }
      });
    },
    onProofUploadDialogClosed() {
      document.removeEventListener("paste", this.handleProofPaste);
      this.proofUploading = false;
    },
    isProofImageFile(file) {
      if (!file) {
        return false;
      }
      if (file.type && file.type.indexOf("image/") === 0) {
        return true;
      }
      let name = (file.name || "").toLowerCase();
      return /\.(jpg|jpeg|png|gif|webp|bmp)$/.test(name);
    },
    beforeProofImageUpload(file) {
      if (!this.isProofImageFile(file)) {
        this.$message.error("请上传图片文件");
        return false;
      }
      return true;
    },
    handleProofPaste(event) {
      if (!this.proofUploadVisible || this.proofUploading) {
        return;
      }
      let items = event.clipboardData && event.clipboardData.items;
      if (!items || !items.length) {
        return;
      }
      for (let i = 0; i < items.length; i++) {
        let item = items[i];
        if (item.kind === "file" && item.type.indexOf("image/") === 0) {
          event.preventDefault();
          let file = item.getAsFile();
          if (file) {
            this.uploadProofImageFile(file);
          }
          return;
        }
      }
    },
    uploadProofImageFile(file) {
      if (!this.isProofImageFile(file)) {
        this.$message.error("请粘贴图片文件");
        return;
      }
      let formData = new FormData();
      formData.append("file", file);
      formData.append("bizModule", "payOrder");
      this.proofUploading = true;
      ossModules
        .upload(formData)
        .then(res => {
          if (res && res.objData && res.objData.fileUrl) {
            this.proofUploadUrls.push(res.objData.fileUrl);
            return;
          }
          this.$message.error((res && res.message) || "上传失败");
        })
        .catch(err => {
          this.$message.error((err && err.message) || "上传失败");
        })
        .finally(() => {
          this.proofUploading = false;
        });
    },
    handleProofUploadSuccess(res) {
      if (res && res.objData && res.objData.fileUrl) {
        this.proofUploadUrls.push(res.objData.fileUrl);
        return;
      }
      this.$message.error("上传失败");
    },
    removeProofUploadImage(index) {
      this.proofUploadUrls.splice(index, 1);
    },
    saveProofUpload() {
      if (!this.proofUploadRow || !this.proofUploadField) {
        return;
      }
      if (!this.proofUploadUrls.length) {
        this.$message.error("请先上传图片");
        return;
      }
      let payload = {
        paymentGroupId: this.proofUploadRow.paymentGroupId,
      };
      payload[this.proofUploadField] = JSON.stringify(this.proofUploadUrls);
      this.proofUploadSaving = true;
      payOrderModules
        .updatePayOrderGroupAttachment(payload)
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "保存失败");
            return;
          }
          this.$message.success("保存成功");
          this.proofUploadVisible = false;
          this.getData();
        })
        .catch(err => {
          this.$message.error(err.message || "保存失败");
        })
        .finally(() => {
          this.proofUploadSaving = false;
        });
    },
    canVoidOrder(row) {
      return row.orderStatus !== PAY_ORDER_STATUS.VOID && row.orderStatus !== PAY_ORDER_STATUS.PAYMENT_COMPLETED;
    },
    canEditOrder(row) {
      return row.orderStatus !== PAY_ORDER_STATUS.PAYMENT_COMPLETED;
    },
    onStatusTabChange(tab) {
      this.activeStatusTab = tab.name;
      this.params.beginIndex = 0;
      this.currentPage = 1;
      this.clearPayOrderSelection();
      this.getData();
    },
    queryBtn() {
      this.params.beginIndex = 0;
      this.currentPage = 1;
      this.fetchStatusCounts();
      this.getData();
    },
    handlerPageChange(page) {
      this.currentPage = page;
      this.params.beginIndex = (page - 1) * this.params.pageSize;
      this.getData();
    },
    buildFilterParams() {
      let query = {
        shopName: this.params.shopName || "",
        supplierId: this.params.supplierId,
        productType: this.params.productType,
        paymentGroupId: (this.params.paymentGroupId || "").trim(),
        sourceType: this.params.sourceType || "",
        invoiceStatus: this.params.invoiceStatus || "",
      };
      if (this.createDate && this.createDate.length === 2) {
        query.beginCreateDate = this.createDate[0].format("yyyy-MM-dd");
        query.endCreateDate = this.createDate[1].format("yyyy-MM-dd");
      }
      if (this.paymentDate && this.paymentDate.length === 2) {
        query.beginPaymentDate = this.paymentDate[0].format("yyyy-MM-dd");
        query.endPaymentDate = this.paymentDate[1].format("yyyy-MM-dd");
      }
      return query;
    },
    buildQueryParams() {
      let orderStatus = "";
      if (this.activeStatusTab === PAY_ORDER_STATUS.VOID) {
        orderStatus = PAY_ORDER_STATUS.VOID;
      } else if (this.activeStatusTab && this.activeStatusTab !== "all") {
        orderStatus = this.activeStatusTab;
      } else {
        orderStatus = this.params.filterOrderStatus || "";
      }
      return {
        ...this.buildFilterParams(),
        orderStatus,
        beginIndex: this.params.beginIndex,
        pageSize: this.params.pageSize,
      };
    },
    fetchStatusCounts() {
      payOrderModules.fetchPayOrderStatusCounts(this.buildFilterParams()).then(res => {
        if (res.mdata && res.mdata.statusCounts) {
          this.statusCounts = {
            ...this.statusCounts,
            ...res.mdata.statusCounts,
          };
        }
      });
    },
    getData() {
      this.loading = true;
      payOrderModules
        .fetchPayOrders(this.buildQueryParams())
        .then(res => {
          this.tableData = (res.ldata || []).map((row, index) => ({
            ...row,
            _tableRowKey: (row.paymentGroupId || "pay-order") + "-" + index,
          }));
          this.total = res.total || 0;
        })
        .catch(err => {
          this.$message.error(err.message || "查询失败");
        })
        .finally(() => {
          this.loading = false;
          this.clearPayOrderSelection();
          this.relayoutTable();
        });
    },
    payOrderRowSelectable(row) {
      return row._paymentRowSpan > 0;
    },
    handlePayOrderSelectionChange(selection) {
      let selectedMap = {};
      this.selectedPayOrders = (selection || []).filter(row => {
        if (!row.paymentGroupId || row._paymentRowSpan <= 0 || selectedMap[row.paymentGroupId]) {
          return false;
        }
        selectedMap[row.paymentGroupId] = true;
        return true;
      });
    },
    clearPayOrderSelection() {
      this.selectedPayOrders = [];
      if (this.$refs.payOrderTable) {
        this.$refs.payOrderTable.clearSelection();
      }
    },
    validateInvoiceSelectionSameShopAndSupplier() {
      let supplierIds = new Set();
      let shopTypes = new Set();
      this.selectedPayOrders.forEach(row => {
        if (row.supplierId != null && row.supplierId !== "") {
          supplierIds.add(String(row.supplierId));
        }
        collectShopTypesFromPayOrderRow(row).forEach(shopType => shopTypes.add(String(shopType)));
      });
      if (supplierIds.size > 1 || shopTypes.size > 1) {
        this.$message.error("请选择同一店铺、同一供应商的支付订单");
        return false;
      }
      return true;
    },
    validateInvoiceSelectionSameSourceType() {
      let sourceTypes = new Set();
      this.selectedPayOrders.forEach(row => {
        sourceTypes.add(normalizePayOrderSourceType(row.sourceType));
      });
      if (sourceTypes.size > 1) {
        this.$message.error("请选择同一业务类型的支付订单");
        return false;
      }
      return true;
    },
    onGenerateInvoice() {
      if (!this.selectedPayOrders.length) {
        this.$message.warning("请先选择支付订单");
        return;
      }
      if (!this.validateInvoiceSelectionSameShopAndSupplier()) {
        return;
      }
      if (!this.validateInvoiceSelectionSameSourceType()) {
        return;
      }
      let paymentGroupIds = this.selectedPayOrders.map(row => row.paymentGroupId);
      this.invoicePreviewLoading = true;
      this.invoicePreviewVisible = true;
      this.invoicePreviewLines = [];
      invoiceModules
        .previewInvoiceFromPayOrders({
          paymentGroupIds: JSON.stringify(paymentGroupIds),
        })
        .then(res => {
          if (res.result !== 1) {
            this.invoicePreviewVisible = false;
            this.$message.error(res.message || "生成预览失败");
            return;
          }
          let mdata = res.mdata || {};
          let invoiceInfoList = mdata.invoiceInfoList || [];
          this.invoicePreviewForm = {
            supplierId: mdata.supplierId || "",
            supplierName: mdata.supplierName || "",
            paymentGroupIds: mdata.paymentGroupIds || paymentGroupIds,
            invoiceInfoId: invoiceInfoList.length ? invoiceInfoList[0].invoiceInfoId : "",
            invoiceTaxRate: mdata.invoiceTaxRate != null ? mdata.invoiceTaxRate : 0,
            invoiceAmount: mdata.invoiceAmount != null ? mdata.invoiceAmount : mdata.totalWithTax || 0,
            supplierInvoiceInfoList: invoiceInfoList,
          };
          this.invoicePreviewLines = res.ldata || [];
        })
        .catch(() => {
          this.invoicePreviewVisible = false;
          this.$message.error("生成预览失败");
        })
        .finally(() => {
          this.invoicePreviewLoading = false;
        });
    },
    getInvoicePreviewSummaries(param) {
      const { columns, data } = param;
      const sums = [];
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = "合计";
          return;
        }
        if (column.property === "amount") {
          sums[index] = this.formatMoney(data.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0));
          return;
        }
        if (column.property === "taxAmount") {
          sums[index] = this.formatMoney(data.reduce((sum, row) => sum + (parseFloat(row.taxAmount) || 0), 0));
          return;
        }
        sums[index] = "";
      });
      return sums;
    },
    saveInvoicePreview() {
      if (!this.invoicePreviewLines.length) {
        this.$message.warning("开票明细为空");
        return;
      }
      if (this.invoicePreviewSupplierInvoiceInfoList.length && !this.invoicePreviewForm.invoiceInfoId) {
        this.$message.warning("请选择开票信息");
        return;
      }
      this.invoicePreviewSaving = true;
      invoiceModules
        .createInvoiceFromPayOrders({
          supplierId: this.invoicePreviewForm.supplierId || "",
          supplierName: this.invoicePreviewForm.supplierName || "",
          invoiceInfoId: this.invoicePreviewForm.invoiceInfoId || "",
          paymentGroupIds: JSON.stringify(this.invoicePreviewForm.paymentGroupIds || []),
          lines: JSON.stringify(this.invoicePreviewLines),
        })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "生成开票记录失败");
            return;
          }
          this.$message.success("开票记录已生成，状态为待开票");
          this.invoicePreviewVisible = false;
          this.clearPayOrderSelection();
          this.getData();
        })
        .catch(() => {
          this.$message.error("生成开票记录失败");
        })
        .finally(() => {
          this.invoicePreviewSaving = false;
        });
    },
    onApproveGroup(row) {
      let label = this.getPassButtonLabel(row.orderStatus);
      this.$confirm(`确认${label}？`, "提示", { type: "warning" }).then(() => {
        this.updateGroupStatus(row.paymentGroupId, "pass", `${label}成功`);
      });
    },
    onRejectGroup(row) {
      this.rejectPaymentGroupId = row.paymentGroupId;
      this.rejectTargetStatus = PAY_ORDER_STATUS_REJECT_TARGET[row.orderStatus] || PAY_ORDER_STATUS.PENDING_ORDER;
      this.rejectReason = "";
      this.rejectDialogVisible = true;
    },
    submitRejectGroup() {
      if (!this.rejectReason || !this.rejectReason.trim()) {
        this.$message.error("请输入打回原因");
        return;
      }
      this.rejectSubmitting = true;
      payOrderModules
        .updatePayOrderGroupStatus({
          paymentGroupId: this.rejectPaymentGroupId,
          action: "reject",
          rejectReason: this.rejectReason.trim(),
        })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "操作失败");
            return;
          }
          this.$message.success("打回成功");
          this.rejectDialogVisible = false;
          this.fetchStatusCounts();
          this.getData();
        })
        .catch(err => {
          this.$message.error(err.message || "操作失败");
        })
        .finally(() => {
          this.rejectSubmitting = false;
        });
    },
    updateGroupStatus(paymentGroupId, action, successText, extraPayload) {
      payOrderModules
        .updatePayOrderGroupStatus({ paymentGroupId, action, ...(extraPayload || {}) })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "操作失败");
            return;
          }
          this.$message.success(successText);
          this.fetchStatusCounts();
          this.getData();
        })
        .catch(err => {
          this.$message.error(err.message || "操作失败");
        });
    },
    onViewProductPriceHistory(row) {
      let productType = ~~row.productType;
      if (!productType) {
        this.$message.info("无法识别产品");
        return;
      }
      this.priceHistoryProductType = productType;
      this.priceHistoryProductName = row.styleName || "";
      this.priceHistoryPaymentGroupId = row.paymentGroupId || "";
      this.priceHistoryPage = 1;
      this.priceHistoryList = [];
      this.priceHistoryTotal = 0;
      this.priceHistoryVisible = true;
      this.fetchProductPriceHistory();
    },
    onPriceHistoryPageChange(page) {
      this.priceHistoryPage = page;
      this.fetchProductPriceHistory();
    },
    priceHistoryRowClassName({ row }) {
      if (row.isCurrent) {
        return "price-history-current";
      }
      return "";
    },
    fetchProductPriceHistory() {
      this.priceHistoryLoading = true;
      payOrderModules
        .fetchPayOrderProductPriceHistory({
          productType: this.priceHistoryProductType,
          paymentGroupId: this.priceHistoryPaymentGroupId,
          beginIndex: (this.priceHistoryPage - 1) * this.priceHistoryPageSize,
          pageSize: this.priceHistoryPageSize,
        })
        .then(res => {
          this.priceHistoryList = res.ldata || [];
          this.priceHistoryTotal = res.total || 0;
          if (res.mdata && res.mdata.productName && !this.priceHistoryProductName) {
            this.priceHistoryProductName = res.mdata.productName;
          }
        })
        .catch(err => {
          this.priceHistoryList = [];
          this.priceHistoryTotal = 0;
          this.$message.error((err && err.message) || "获取历史采购记录失败");
        })
        .finally(() => {
          this.priceHistoryLoading = false;
        });
    },
    onViewPurchaseRecords(row) {
      payOrderModules.fetchPayOrderPurchaseRecords({ paymentGroupId: row.paymentGroupId }).then(res => {
        this.purchaseRecordList = res.ldata || [];
        if (!this.purchaseRecordList.length) {
          this.$message.info("暂无关联采购记录");
          return;
        }
        this.purchaseRecordVisible = true;
      });
    },
    onViewPurchaseMaterials(row) {
      this.purchaseMaterialVisible = true;
      this.purchaseMaterialLoading = true;
      this.purchaseMaterialList = [];
      payOrderModules
        .fetchPayOrderPurchaseMaterials({ paymentGroupId: row.paymentGroupId })
        .then(res => {
          if (res.result !== 1) {
            this.purchaseMaterialVisible = false;
            this.$message.error(res.message || "获取采购材料失败");
            return;
          }
          this.purchaseMaterialList = res.ldata || [];
          if (!this.purchaseMaterialList.length) {
            this.purchaseMaterialVisible = false;
            this.$message.info("暂无关联采购材料");
          }
        })
        .catch(err => {
          this.purchaseMaterialVisible = false;
          this.$message.error(err.message || "获取采购材料失败");
        })
        .finally(() => {
          this.purchaseMaterialLoading = false;
        });
    },
    showOpreateLog(row) {
      this.operateHistory = [];
      this.opreateLogVisible = true;
      requestModules
        .fetchOpreateLogs({
          startTime: 0,
          endTime: new Date().getTime(),
          bussinessId: row.paymentGroupId,
          beginIndex: 0,
          pageSize: 1000,
        })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "获取操作记录失败");
            return;
          }
          this.operateHistory = res.ldata || [];
        })
        .catch(err => {
          this.$message.error(err.message || "获取操作记录失败");
        });
    },
    onEditGroup(row) {
      let groupRow = row;
      if (row._paymentRowSpan <= 0) {
        groupRow = this.tableData.find(item => item.paymentGroupId === row.paymentGroupId && item._paymentRowSpan > 0) || row;
      }
      this.payOrderForm = {
        paymentGroupId: groupRow.paymentGroupId,
        supplierId: groupRow.supplierId || "",
        freight: groupRow.freight || 0,
        platformDiscount: groupRow.platformDiscount || 0,
        returnExchangeFee: groupRow.returnExchangeFee || 0,
        otherFee: groupRow.otherFee || 0,
        orderPlatform: groupRow.orderPlatform || "对公",
        actualPaidAmount: groupRow.actualPaidAmount || 0,
        payableAmount: groupRow.payableAmount || 0,
        orderStatus: groupRow.orderStatus || PAY_ORDER_STATUS.PENDING_ORDER,
        orderConfirmProof: this.normalizeProofImageList(groupRow.orderConfirmProof),
        paymentScreenshot: this.normalizeProofImageList(groupRow.paymentScreenshot),
        remark: groupRow.remark || "",
      };
      this.payOrderPreviewList = [];
      this.payOrderPreviewShouldSyncActualPaid = false;
      this.editDialogVisible = true;
      this.loadPayOrderPreview();
    },
    saveEditGroup() {
      if (!this.payOrderForm.supplierId) {
        this.$message.error("请选择采购供应商");
        return;
      }
      if (!this.payOrderPreviewList.length) {
        this.$message.error("没有可保存的明细");
        return;
      }

      let supplier = this.supplierList.find(item => item.supplierId == this.payOrderForm.supplierId);
      let supplierName = supplier ? supplier.supplierShortName || supplier.supplierName : "";
      let freight = parseFloat(this.payOrderForm.freight) || 0;
      let platformDiscount = parseFloat(this.payOrderForm.platformDiscount) || 0;
      let returnExchangeFee = parseFloat(this.payOrderForm.returnExchangeFee) || 0;
      let otherFee = parseFloat(this.payOrderForm.otherFee) || 0;
      let linePayableAmount = this.payOrderPreviewList.reduce((sum, row) => sum + (Number(row.payableAmount) || 0), 0);
      let totalAmount = parseFloat((linePayableAmount + freight - platformDiscount + returnExchangeFee + otherFee).toFixed(2));

      let lineItems = this.payOrderPreviewList.map((line, index) => ({
        _id: line._id || "",
        purchaseDate: line.purchaseDate || "",
        shopName: line.shopName || "",
        styleName: line.productName,
        supplierName,
        supplierId: this.payOrderForm.supplierId,
        productType: line.productType,
        unitPrice: line.unitPrice,
        quantity: line.purchaseQuantity,
        totalPrice: line.purchaseAmount,
        taxRate: line.taxRate,
        taxAmount: line.taxAmount,
        freight: index === 0 ? freight : 0,
        fullReduction: 0,
        platformDiscount: index === 0 ? platformDiscount : 0,
        returnExchangeFee: index === 0 ? returnExchangeFee : 0,
        otherFee: index === 0 ? otherFee : 0,
        totalAmount: index === 0 ? totalAmount : line.payableAmount,
        purchaseRecords: line.purchaseRecords || [],
      }));

      this.payOrderSubmitLoading = true;
      payOrderModules
        .savePayOrderGroup({
          paymentGroupId: this.payOrderForm.paymentGroupId,
          actualPaidAmount: this.payOrderForm.actualPaidAmount,
          orderPlatform: this.payOrderForm.orderPlatform,
          orderStatus: this.payOrderForm.orderStatus,
          orderConfirmProof: JSON.stringify(this.normalizeProofImageList(this.payOrderForm.orderConfirmProof)),
          paymentScreenshot: JSON.stringify(this.normalizeProofImageList(this.payOrderForm.paymentScreenshot)),
          remark: this.payOrderForm.remark,
          payOrderList: JSON.stringify(lineItems),
          deleteIds: JSON.stringify([]),
        })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "保存失败");
            return;
          }
          this.$message.success("保存成功");
          this.editDialogVisible = false;
          this.fetchStatusCounts();
          this.getData();
        })
        .catch(err => {
          this.$message.error(err.message || "保存失败");
        })
        .finally(() => {
          this.payOrderSubmitLoading = false;
        });
    },
    voidPayOrder(paymentGroupId) {
      payOrderModules.voidPayOrderGroup({ paymentGroupId }).then(res => {
        if (res.result !== 1) {
          this.$message.error(res.message || "作废失败");
          return;
        }
        this.$message.success("作废成功");
        this.editDialogVisible = false;
        this.fetchStatusCounts();
        this.getData();
      });
    },
    onVoidGroup(row) {
      this.$confirm("确认作废该支付单？", "提示", { type: "warning" }).then(() => {
        this.voidPayOrder(row.paymentGroupId);
      });
    },
    onVoidGroupInDialog() {
      this.$confirm("确认作废整单？", "提示", { type: "warning" }).then(() => {
        this.voidPayOrder(this.payOrderForm.paymentGroupId);
      });
    },
  },
};
</script>

<style lang="scss">
@import "../../assets/styles/purchaseMgmtPage.scss";

#PayOrderMgmt {
  .pay-order-table {
    .row-item {
      min-height: 16px;
      line-height: 16px;
    }

    .row-sub {
      color: #909399;
      font-size: 11px;
    }

    .table-cell {
      padding: 5px 2px;
      font-size: 12px;
      color: #606266;
    }

    .el-table__footer-wrapper td {
      background: #fafafa !important;
      font-weight: 600;
      color: #303133;
      font-size: 12px;
    }

    .price-history-link {
      color: #409eff;
      cursor: pointer;
    }

    .price-history-link:hover {
      text-decoration: underline;
    }

    .money-text {
      &.positive {
        color: #67c23a;
      }

      &.negative {
        color: #f56c6c;
      }

      &.zero {
        color: #909399;
      }
    }

    tr:hover > td {
      background-color: #f5f9ff !important;
    }

    tr.el-table__row--striped td.merge-col {
      background-color: #f3f8ff !important;
    }

    .proof-thumb-list {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      justify-content: center;
      padding: 2px 0;
    }

    .proof-thumb {
      width: 40px;
      height: 40px;
      border-radius: 4px;
      vertical-align: middle;
    }

    .proof-empty {
      color: #c0c4cc;
    }
  }

  .action-btn {
    &.success {
      color: #67c23a;
    }

    &.danger {
      color: #f56c6c;
    }

    &.warning {
      color: #e6a23c;
    }
  }

  .page-toolbar {
    flex-shrink: 0;
    margin-bottom: 12px;
  }

  .action-cell {
    flex-wrap: nowrap;
    white-space: nowrap;
  }

  .pagination-box {
    padding-top: 12px;
    min-height: 0;

    .el-pagination {
      padding: 0;
      font-weight: 400;

      &.is-background .btn-prev,
      &.is-background .btn-next,
      &.is-background .el-pager li {
        min-width: 28px;
        height: 28px;
        line-height: 28px;
        margin: 0 3px;
        font-size: 12px;
      }

      button,
      span:not([class*="suffix"]) {
        height: 28px;
        line-height: 28px;
        font-size: 12px;
      }
    }
  }
}

.pay-order-action-dropdown {
  .el-dropdown-item.success {
    color: #67c23a;
  }

  .el-dropdown-item.warning {
    color: #e6a23c;
  }

  .el-dropdown-item.danger {
    color: #f56c6c;
  }
}

.pay-order-proof-dialog {
  .proof-upload-panel {
    outline: none;
  }

  .proof-upload-panel:focus {
    outline: none;
  }

  .proof-upload-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    min-height: 120px;
  }

  .proof-upload-item {
    position: relative;
    width: 120px;
    height: 120px;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #ebeef5;
  }

  .proof-upload-image {
    width: 120px;
    height: 120px;
  }

  .proof-upload-remove {
    position: absolute;
    top: 4px;
    right: 4px;
    font-size: 18px;
    color: #f56c6c;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.92);
    border-radius: 50%;
  }

  .proof-upload-add .el-upload {
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
  }

  .proof-upload-add .el-upload:hover {
    border-color: #409eff;
  }

  .proof-upload-trigger {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 120px;
    height: 120px;
  }

  .avatar-uploader-icon {
    font-size: 28px;
    color: #8c939d;
    line-height: 1;
    text-align: center;
  }

  .proof-upload-text {
    margin-top: 8px;
    font-size: 12px;
    color: #909399;
  }

  .proof-upload-tip {
    margin-top: 12px;
    font-size: 12px;
    color: #909399;
    text-align: center;
    line-height: 1.6;
  }
}

.pay-order-edit-dialog {
  .el-dialog__body {
    padding-top: 8px;
  }

  .pay-order-section {
    margin-bottom: 16px;
  }

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;
    padding-left: 8px;
    border-left: 3px solid #409eff;
  }

  .pay-order-form {
    padding: 16px;
    background: #f8f9fb;
    border-radius: 6px;
    border: 1px solid #ebeef5;

    .form-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 12px 24px;
      margin-bottom: 16px;
    }

    .form-item {
      display: flex;
      align-items: center;
      min-width: 240px;

      &.wide {
        width: 100%;
      }

      .el-select,
      .el-input {
        width: 180px;
      }
    }

    .form-label {
      display: inline-block;
      width: 80px;
      flex-shrink: 0;
      color: #606266;
      font-size: 13px;
    }

    .tax-label {
      margin-left: 12px;
      color: #909399;
      font-size: 12px;
    }

    .amount-summary {
      padding-top: 12px;
      border-top: 1px dashed #dcdfe6;
    }

    .remark-item {
      margin-top: 16px;
      align-items: flex-start;

      .form-label {
        margin-top: 6px;
      }

      .el-textarea {
        flex: 1;
      }
    }

    .amount-row {
      display: flex;
      align-items: center;
      min-height: 36px;
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .amount-label {
      width: 80px;
      flex-shrink: 0;
      color: #606266;
      font-size: 13px;
    }

    .amount-value {
      font-size: 16px;
      font-weight: 600;

      &.payable {
        color: #409eff;
      }

      &.positive {
        color: #67c23a;
      }

      &.negative {
        color: #f56c6c;
      }

      &.zero {
        color: #909399;
      }
    }

    .amount-input {
      width: 180px;
    }

    .amount-tip {
      margin-left: 10px;
      color: #909399;
      font-size: 12px;
    }
  }
}

.pay-order-invoice-dialog {
  .invoice-preview-config {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
    padding: 14px 16px;
    background: #fafafa;
    border: 1px solid #ebeef5;
    border-radius: 8px;
  }

  .invoice-preview-config-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .invoice-preview-config-label {
    width: 88px;
    flex-shrink: 0;
    color: #606266;
    font-size: 13px;
    font-weight: 600;
    text-align: right;
  }

  .invoice-preview-config-value {
    color: #303133;
    font-size: 13px;
  }

  .invoice-preview-config-select {
    width: 420px;
  }

  .invoice-preview-config-tip {
    padding: 10px 12px;
    color: #e6a23c;
    font-size: 12px;
    background: #fdf6ec;
    border: 1px solid #faecd8;
    border-radius: 6px;
  }

  .invoice-preview-info-descriptions {
    width: 100%;
    background: #fff;
  }

  .invoice-preview-panel {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .invoice-preview-table {
    background: #fff;
  }

  .invoice-preview-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
    padding: 12px 16px;
    background: linear-gradient(135deg, #fafafa 0%, #f5f7fa 100%);
    border: 1px solid #ebeef5;
    border-radius: 8px;
  }

  .invoice-preview-footer-label {
    color: #303133;
    font-size: 14px;
    font-weight: 600;
  }

  .invoice-preview-footer-value {
    color: #409eff;
    font-size: 20px;
    font-weight: 700;
  }
}

.pay-order-price-history-dialog {
  .record-table {
    .el-table__header th {
      background: #f5f7fa;
      color: #303133;
      font-weight: 600;
    }
  }

  .price-history-current td {
    background: #ecf5ff !important;
  }

  .price-history-pagination {
    margin-top: 12px;
    text-align: right;
  }
}

.pay-order-record-dialog {
  .record-table {
    .el-table__header th {
      background: #f5f7fa;
      color: #303133;
      font-weight: 600;
    }
  }
}

.pay-order-reject-dialog {
  .reject-dialog-tip {
    margin-bottom: 12px;
    color: #606266;
    font-size: 13px;
  }
}

.remark-cell {
  line-height: 1.5;
  word-break: break-all;

  .reject-reason-text {
    margin-top: 4px;
    color: #e6a23c;
    font-size: 12px;
  }
}
</style>
